<template>
    <form
        class="flex flex-col gap-y-4"
        @submit="handleSubmitCheckoutForm"
    >
        <customer-information
            v-model:editing="editingCustomerInformation"
            :customer="primaryCustomer"
            :facility="facility"
            show-errors
        />

        <checkout-section-separator />

        <billing-address
            v-model:editing="editingBillingAddress"
            :customer="primaryCustomer"
            :is-full-address="fullAddressRequired"
            :title="t('checkout.billingAddress')"
        />

        <checkout-section-separator v-if="readyForRecurringToken" />

        <recurring-information
            v-if="requiresRecurringToken && readyForRecurringToken"
            ref="recurringInformationRef"
            v-model:editing="editingRecurringInformation"
            :customer="primaryCustomer"
            :facility="facility"
            :recurring-total="recurringTotal"
            :payment-method-input="paymentMethodInput"
        />

        <document-section
            v-if="readyForRecurringToken"
            :contract="contract"
            :is-signing-document="isValidating || isPending"
            balance-due-mode
            :invalid-initials="invalidInitials"
            :invalid-signatures="invalidSignatures"
            @inline-checkout-document="handleInlineCheckoutDocument"
            @inline-checkout="handleInlineCheckout"
        />

        <div
            v-if="submitCount > 0 && Object.keys(errors).length > 0"
            class="text-red-600 mt-4 font-medium"
        >
            {{ t('checkout.pleaseFixErrorsInRedAbove') }}
        </div>

        <prime-button
            v-if="emailRequiredForPayment && !readyForRecurringToken"
            :label="t('checkout.continueToPayment')"
            type="submit"
            class="w-full"
            :loading="isValidating || isPending"
        />
    </form>
</template>

<script setup lang="ts">
import { toTypedSchema } from '@vee-validate/yup'
import { object } from 'yup'
import { useForm } from 'vee-validate'
import { useRpQuery, UserReportableError } from '~/composables/graphql'
import { useMutation } from '@tanstack/vue-query'
import type {
    CheckoutRecurringInput,
    CommitCheckoutInput,
    DocumentInput
} from '~/resources/graphql/graphql'
import { computed, onMounted, ref, toRef, watch } from 'vue'
import { type FragmentType, graphql } from '~/resources/graphql'
import { useKioskSuccess } from '~/composables/use-kiosk-success'
import { useActiveFacility, useShowAlert } from '#imports'
import { handleValidationErrors } from '~/components/PurchaseContract/DocumentSection/handle-validation-errors'

import {
    CheckoutFormCheckoutFragment,
    CheckoutFormQueryFragment,
    useCheckoutForm
} from '~/components/Checkout/Forms/use-checkout-form'

import CustomerInformation, {
    CustomerInformationCheckoutFormInitialValues,
    CustomerInformationCheckoutFormSchema
} from '~/components/Checkout/Sections/CustomerInformation.vue'
import BillingAddress, {
    BillingAddressInitialValues,
    BillingAddressSchema
} from '~/components/Checkout/Sections/BillingAddress.vue'
import RecurringInformation, {
    RecurringInformationInitialValues,
    RecurringInformationSchema
} from '~/components/Checkout/Sections/RecurringInformation.vue'
import DocumentSection, {
    DocumentSectionContractFragment
} from '~/components/PurchaseContract/DocumentSection/DocumentSection.vue'
import { GraphQLRequestError } from 'awesome-graphql-client'
import CheckoutSectionSeparator from '~/components/Checkout/Forms/CheckoutSectionSeparator.vue'
import { checkoutRecurringInput } from '~/components/Checkout/Forms/checkout-helpers'
import PrimeButton from 'primevue/button'
import { usePaymentMethodInput } from '~/components/Checkout/Sections/use-payment-method-input'
import { useI18n } from 'vue-i18n'
import { useFingerprintCheckoutEventPayload } from '~/composables/use-fingerprint-visitor-id'

const props = defineProps<{
    query: FragmentType<typeof CheckoutFormQueryFragment>
    checkout: FragmentType<typeof CheckoutFormCheckoutFragment>
    contract?: FragmentType<typeof DocumentSectionContractFragment>
}>()

const { t } = useI18n({ useScope: 'global' })

const {
    checkoutId,

    primaryCustomer,
    facility,

    fullAddressRequired,
    requiresCustomerInformation,

    editingCustomerInformation,
    editingBillingAddress,
    editingRecurringInformation,

    recurringTotal,
    requiresRecurringToken,

    incrementProcessing,
    decrementProcessing
} = useCheckoutForm(toRef(props, 'query'), toRef(props, 'checkout'))

const schema = toTypedSchema(
    object({
        customer: CustomerInformationCheckoutFormSchema(t),
        address: BillingAddressSchema(t),
        recurring: RecurringInformationSchema(t)
    })
)

const activeFacility = useActiveFacility()
const {
    handleSubmit,
    errors,
    submitCount,
    setFieldError,
    validate,
    validateField
} = useForm({
    validationSchema: schema,
    initialValues: {
        customer: CustomerInformationCheckoutFormInitialValues(
            primaryCustomer.value,
            facility.value,
            activeFacility.value.organization.onlineGuestId
        ),
        address: BillingAddressInitialValues(
            primaryCustomer.value,
            fullAddressRequired.value
        ),
        recurring: RecurringInformationInitialValues(
            requiresRecurringToken.value ? recurringTotal.value : null
        )
    }
})

const emailRequiredForPayment = computed(
    () => requiresCustomerInformation.value && requiresRecurringToken.value
)
const pendingCustomerInformation = ref(emailRequiredForPayment.value)
const readyForRecurringToken = computed(
    () => pendingCustomerInformation.value === false
)
watch(editingCustomerInformation, (newValue, oldValue) => {
    // If we require customer information for the payment methods, when the
    // customer information changes we have to make a new session, so reset the form
    if (emailRequiredForPayment.value) {
        if (oldValue === false && newValue === true) {
            pendingCustomerInformation.value = true
        }
    }
})
watch(editingBillingAddress, (newValue, oldValue) => {
    // If we require customer information for the payment methods, when the
    // customer information changes we have to make a new session, so reset the form
    if (emailRequiredForPayment.value) {
        if (oldValue === false && newValue === true) {
            pendingCustomerInformation.value = true
        }
    }
})

const { customer, address, paymentMethodInput } = usePaymentMethodInput()
onMounted(() => {
    if (emailRequiredForPayment.value) {
        if (customer.value.email !== '' && address.value.postal !== '') {
            pendingCustomerInformation.value = false
        }
    }
})

const isValidating = ref(false)
const recurringInformationRef = ref<InstanceType<typeof RecurringInformation>>()

async function handleSubmitCheckoutForm(event?: SubmitEvent) {
    // Prevent form action
    event?.preventDefault()
    submitCount.value += 1

    isValidating.value = true
    try {
        if (pendingCustomerInformation.value) {
            // Otherwise, validate the entire form
            const result = await validate()
            const isValid = Object.entries(result.results).every(
                ([key, value]) => {
                    return (
                        key.startsWith('payment') ||
                        key.startsWith('recurring') ||
                        value.valid
                    )
                }
            )

            // TODO: Do we need to do this? Also clear this error so we don't flash red on the recurring error
            setFieldError('recurring', undefined)

            // Ready for recurring token or payment!
            if (isValid) {
                submitCount.value = 0 // reset submit count
                editingCustomerInformation.value = false
                editingBillingAddress.value = false
                pendingCustomerInformation.value = false

                // Recurring payment method required?
                if (requiresRecurringToken.value) {
                    editingRecurringInformation.value = true
                }
            } else {
                // TODO: Find a way to scroll up
            }

            return
        }

        // Recurring checkout
        // If recurring field is not valid, submit stripe form
        // We use silent so we don't show the error message until after stripe form is submitted
        const { valid: validRecurring } = await validateField('recurring', {
            mode: 'silent'
        })
        if (!validRecurring) {
            await recurringInformationRef.value.submitRecurringForm()
        }

        return onSubmit(event)
    } finally {
        isValidating.value = false
    }
}

const invalidInitials = ref(new Set<number>())
const invalidSignatures = ref(new Set<number>())

const rpQuery = useRpQuery()
const { mutate, isPending } = useMutation({
    mutationFn: async (mutationData: CommitCheckoutInput) => {
        const response = await rpQuery(
            graphql(/** GraphQL */ `
                mutation InlineSubmitContractCustomer(
                    $input: InlineSubmitContractInput!
                ) {
                    inlineSubmitContract(input: $input) {
                        __typename
                        ... on InlineSubmitContractResult {
                            contract {
                                __typename
                                ... on PlanContract {
                                    referenceNumber
                                    planType
                                    customer {
                                        firstName
                                    }
                                }
                            }
                        }
                        ... on ValidationError {
                            fieldErrors {
                                path
                                message
                            }
                        }
                    }
                }
            `),
            {
                input: mutationData
            }
        )

        if (response.inlineSubmitContract === null) {
            throw new Error(
                t('checkout.inlineCheckoutError', {
                    code: 'CFKCBDM'
                })
            )
        }

        if (
            response.inlineSubmitContract.__typename ===
                'InlineSubmitContractResult' ||
            response.inlineSubmitContract.__typename === 'ValidationError'
        ) {
            return response.inlineSubmitContract
        }

        throw new Error()
    }
})

const kioskSuccessFunction = useKioskSuccess()

// Once form is validated, this preps and calls the mutation
const showAlert = useShowAlert()
const savedDocumentInput = ref()
const { getFingerprintCheckoutEventPayload } =
    useFingerprintCheckoutEventPayload()
const onSubmit = handleSubmit(async values => {
    // Customer and Address
    const customer = {
        ...values.customer,
        dateOfBirth: null,
        mobilePhone: values.customer.mobilePhone.number,
        address: {
            ...values.address,
            // Wipe out the full address
            fullAddress: undefined
        }
    }

    // Add date of birth if it exists
    if (values.customer.dateOfBirth !== '') {
        customer.dateOfBirth = values.customer.dateOfBirth
    }

    // Recurring Token
    let recurring: CheckoutRecurringInput | null = null
    if (requiresRecurringToken.value) {
        recurring = checkoutRecurringInput(values.recurring)
    }

    incrementProcessing()

    // Attach Fingerprint event context only to the checkout submit mutation
    // that needs fraud review context.
    const fingerprintEventPayload = await getFingerprintCheckoutEventPayload()

    mutate(
        {
            checkoutId: checkoutId.value,
            customer,
            recurring,
            document: savedDocumentInput.value,
            fingerprintEventPayload
        },
        {
            onSuccess: data => {
                if (data.__typename === 'ValidationError') {
                    handleValidationErrors(
                        data.fieldErrors,
                        invalidInitials,
                        invalidSignatures
                    )

                    let messageBuilder = ''
                    data.fieldErrors.forEach(error => {
                        if (
                            error.path !== 'initials' &&
                            error.path !== 'signatures'
                        ) {
                            messageBuilder += error.message
                        }
                    })
                    if (messageBuilder !== '') {
                        showAlert(t('headsUp'), messageBuilder)
                    }
                } else {
                    kioskSuccessFunction({
                        includedInvoice: false,
                        wasGuest: false,
                        data: {
                            referenceNumber: data.contract.referenceNumber,
                            planType: data.contract.planType,
                            firstName: data.contract.customer.firstName
                        }
                    })
                }
            },
            onError(error) {
                if (
                    !(error instanceof UserReportableError) &&
                    !(error instanceof GraphQLRequestError)
                ) {
                    showAlert(t('headsUp'), String(error))
                }
            },
            onSettled() {
                decrementProcessing()
            }
        }
    )
})

function handleInlineCheckoutDocument(documentInput: DocumentInput) {
    savedDocumentInput.value = documentInput
}

function handleInlineCheckout(documentInput: DocumentInput) {
    // Save the document
    savedDocumentInput.value = documentInput
    // The handle the submit event
    handleSubmitCheckoutForm()
}
</script>
